import type { ImageOptions } from '@tiptap/extension-image';
import { Plugin } from '@tiptap/pm/state';
import ImageResize from 'tiptap-extension-resize-image';

import type { ImageUploadFunction } from '../../../types';

interface CustomImageOptions extends ImageOptions {
  onImageUpload?: ImageUploadFunction;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    customImage: {
      setCustomImage: (options: {
        src: string;
        alt?: string;
        title?: string;
        content?: string;
      }) => ReturnType;
    };
  }
}

// Copied from: https://github.com/ueberdosis/tiptap/issues/2912
export const ImageExtension = ImageResize.extend<CustomImageOptions>({
  name: 'customImage',
  addOptions() {
    return {
      ...this.parent?.(),
      onImageUpload: undefined,
    };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      content: {
        default: null,
      },
    };
  },
  addCommands() {
    return {
      ...this.parent?.(),
      setCustomImage:
        (options: { src: string; alt?: string; title?: string; content?: string }) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              ...options,
            },
          });
        },
    };
  },
  addProseMirrorPlugins() {
    const imageUploadFunction = this.options.onImageUpload;
    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            drop(view, event) {
              const hasFiles =
                event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length;

              if (!hasFiles) {
                return;
              }

              const images = Array.from(event.dataTransfer.files).filter((file) =>
                /image/i.test(file.type),
              );

              if (images.length === 0) {
                return;
              }

              event.preventDefault();

              const { schema } = view.state;
              const coordinates = view.posAtCoords({
                left: event.clientX,
                top: event.clientY,
              });

              if (!coordinates) {
                return;
              }

              Promise.all(
                images.map((image) => {
                  if (imageUploadFunction) {
                    // then is used to handle async behavior without using async/await
                    imageUploadFunction(image).then((response) => {
                      if (response) {
                        const node = schema.nodes.customImage.create({
                          src: response.url,
                          content: response.fileName,
                        });
                        const transaction = view.state.tr.insert(coordinates.pos, node);
                        view.dispatch(transaction);
                      }
                    });
                  } else {
                    return new Promise((resolve) => {
                      // Fallback to original behavior if no upload function provided
                      const reader = new FileReader();
                      reader.onload = (readerEvent) => {
                        const node = schema.nodes.image.create({
                          src: (readerEvent.target as FileReader).result,
                        });
                        const transaction = view.state.tr.insert(coordinates.pos, node);
                        view.dispatch(transaction);
                        // resolve the promise to indicate that the image has been added
                        resolve(undefined);
                      };
                      reader.readAsDataURL(image);
                    });
                  }
                }),
              ).catch(console.error);
            },
            paste(view, event) {
              const hasFiles =
                event.clipboardData &&
                event.clipboardData.files &&
                event.clipboardData.files.length;

              if (!hasFiles) {
                return;
              }

              const images = Array.from(event.clipboardData.files).filter((file) =>
                /image/i.test(file.type),
              );

              if (images.length === 0) {
                return;
              }

              event.preventDefault();

              const { schema } = view.state;

              Promise.all(
                images.map((image) => {
                  if (imageUploadFunction) {
                    // then is used to handle async behavior without using async/await
                    return imageUploadFunction(image).then((response) => {
                      if (response) {
                        const node = schema.nodes.customImage.create({
                          src: response.url,
                          content: response.fileName,
                        });
                        const transaction = view.state.tr.replaceSelectionWith(node);
                        view.dispatch(transaction);
                      }
                    });
                  } else {
                    return new Promise((resolve) => {
                      const reader = new FileReader();
                      reader.onload = (readerEvent) => {
                        const node = schema.nodes.image.create({
                          src: (readerEvent.target as FileReader).result,
                        });
                        const transaction = view.state.tr.replaceSelectionWith(node);
                        view.dispatch(transaction);
                        // resolve the promise to indicate that the image has been added
                        resolve(undefined);
                      };
                      reader.readAsDataURL(image);
                    });
                  }
                }),
              ).catch(console.error);
            },
          },
        },
      }),
    ];
  },
});
